import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../../functions/useHandleBlur";
import { useFormState } from "../../../../functions/useFormState";
import { selectApiStatus } from "../../../../redux/slices/apiStatusSlice";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../../redux/slices/apiUrlSlice";
import {
  selectInletScreen,
  selectFieldDescriptions,
} from "../../../../redux/selectors";
import useFieldClass from "../../../../functions/useFieldClass";

const InletScreen = ({ zone }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "inletScreen";

  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const error = null;

  const { options } = useSelector((state) => state.dropdown);

  const storedJobDetails = useSelector(selectInletScreen);
  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const apiStatus = useSelector(selectApiStatus);
  const loading = apiStatus[sectionName] !== "success";

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});
  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        inletFlumePipeSize: jobDetails.InletFlumePipeSize || "",
        filterFlumeType: jobDetails.FilterFlumeType || "",
        filterManufacturer: jobDetails.FilterManufacturer || "",
        inletFilterType: jobDetails.InletFilterType || "",
        inletFilterModel: jobDetails.InletFilterModel || "",
        inletScreenSize: jobDetails.InletScreenSize || "",
        inletFilterComments: jobDetails.InletFilterComments || "",
        quantity: jobDetails.Quantity || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly);
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly;

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  /****************Add dropdown options here*************************/

  const filterManufacturerOptions =
    options?.filter((option) => option.DropdownType === "Filter Manufacturer").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const inletFilterTypeOptions =
    options?.filter((option) => option.DropdownType === "Inlet Filter Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const inletFilterModelOptions =
    options?.filter((option) => option.DropdownType === "Inlet Filter Model").sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const fieldClasses = useFieldClass(formData, zone);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div className="filtration-condensed-grid">
            <Row className="mb-3 filtration-field-row">
              <Col>
                <Form.Group as={Row} controlId="inletFlumePipeSize">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InletFlumePipeSize")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Flume Pipe Size</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="inletFlumePipeSize"
                      value={formData.inletFlumePipeSize || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Inlet Flume Pipe Size",
                          [],
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "InletFlumePipeSize",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.inletFlumePipeSize}
                      onChange={handleChange}
                      placeholder="Enter Inlet Flume Pipe Size"
                    />
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="filterFlumeType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("FilterFlumeType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Flume Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="filterFlumeType"
                      value={formData.filterFlumeType || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Filter Flume Type",
                          [],
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "FilterFlumeType",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.filterFlumeType}
                      onChange={handleChange}
                      placeholder="Enter Filter Flume Type"
                    />
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="filterManufacturer">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("FilterManufacturer")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Manufacturer</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="filterManufacturer"
                      value={formData.filterManufacturer || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Filter Manufacturer",
                          filterManufacturerOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "FilterManufacturer",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.filterManufacturer}
                      onChange={handleChange}
                    >
                      <option value="">Select Filter Manufacturer</option>
                      {filterManufacturerOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.filterManufacturer,
                        filterManufacturerOptions
                      ) &&
                        formData.filterManufacturer && (
                          <option
                            value={formData.filterManufacturer}
                            style={{ color: "red" }}
                          >
                            *{formData.filterManufacturer}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3 filtration-field-row">
              <Col>
                <Form.Group as={Row} controlId="inletFilterType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InletFilterType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Filter Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="inletFilterType"
                      value={formData.inletFilterType || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Inlet Filter Type",
                          inletFilterTypeOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "InletFilterType",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.inletFilterType}
                      onChange={handleChange}
                    >
                      <option value="">Select Inlet Filter Type</option>
                      {inletFilterTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.inletFilterType,
                        inletFilterTypeOptions
                      ) &&
                        formData.inletFilterType && (
                          <option
                            value={formData.inletFilterType}
                            style={{ color: "red" }}
                          >
                            *{formData.inletFilterType}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="inletFilterModel">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InletFilterModel")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Filter Model</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="inletFilterModel"
                      value={formData.inletFilterModel || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Inlet Filter Model",
                          inletFilterModelOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "InletFilterModel",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.inletFilterModel}
                      onChange={handleChange}
                    >
                      <option value="">Select Inlet Filter Model</option>
                      {inletFilterModelOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.inletFilterModel,
                        inletFilterModelOptions
                      ) &&
                        formData.inletFilterModel && (
                          <option
                            value={formData.inletFilterModel}
                            style={{ color: "red" }}
                          >
                            *{formData.inletFilterModel}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="inletScreenSize">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InletScreenSize")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Screen Size</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="inletScreenSize"
                      value={formData.inletScreenSize || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Inlet Screen Size",
                          [],
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "InletScreenSize",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.inletScreenSize}
                      onChange={handleChange}
                      placeholder="Enter Inlet Screen Size"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3 filtration-field-row">
              <Col md={4}>
                <Form.Group as={Row} controlId="quantity">
                  <Form.Label column md={4}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Quantity")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Quantity</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="quantity"
                      value={formData.quantity || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Quantity",
                          [],
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "Quantity",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.quantity}
                      onChange={handleChange}
                      placeholder="Enter Quantity"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3 filtration-field-row filtration-comments-row">
              <Col>
                <Form.Group controlId="inletFilterComments">
                  <Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InletFilterComments")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Comments</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="inletFilterComments"
                    value={formData.inletFilterComments || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Inlet Filter Comments",
                        [],
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "InletFilterComments",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.inletFilterComments}
                    onChange={handleChange}
                    placeholder="Enter Inlet Filter Comments"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default InletScreen;
